/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signInWithPopup, createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from './firebase';
import {
    loadQuizProgress,
    markQuizCompleted,
    saveSlideProgress,
    saveQuizAnswer,
    saveQuizProgress
} from '../utils/quizUtilityNew';
import CertificateGenerator from './CertificateGenerator';
import './PeerSupportCourse.css';

const CollegeStudentCourse = () => {
    const [user, loading] = useAuthState(auth);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [quizAnswers, setQuizAnswers] = useState({});
    const [showResults, setShowResults] = useState(false);
    const [progressLoaded, setProgressLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showCertificate, setShowCertificate] = useState(false);

    // Track if this is the initial load to prevent saving initial state
    const isInitialMount = useRef(true);

    // Authentication modal state
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
    const [isSignUp, setIsSignUp] = useState(false);
    const [authForm, setAuthForm] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [resetEmail, setResetEmail] = useState('');
    const [authError, setAuthError] = useState('');
    const [resetError, setResetError] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);
    const [isAuthLoading, setIsAuthLoading] = useState(false);

    // College Student Course content slides
    const slides = [
        {
            id: 0,
            content: (
                <div className="text-center slide-content">
                    <img src="/assets/logo.png" alt="PilotHelper Logo" className="course-logo mx-auto d-block" />
                    <h1 className="display-4 mb-4">Welcome to The College Pilot Peer Support Course</h1>
                    <h2 className="h3 text-primary mb-4">Building a Culture of Support in Aviation</h2>
                </div>
            )
        },
        {
            id: 1,
            title: "Why is This Course Important?",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">Why is This Course Important?</h2>
                    <div className="row d-flex align-items-stretch">
                        <div className="col-md-4 mb-4">
                            <div className="p-3 border rounded bg-primary text-white h-100 d-flex flex-column">
                                <h5>College student pilots face unique challenges:</h5>
                                <ul className="mb-0 flex-grow-1">
                                    <li>Balancing academics with flight training</li>
                                    <li>Financial strain from tuition and flying costs</li>
                                    <li>Social isolation due to restrictions on alcohol/drugs</li>
                                    <li>Pressure to maintain strict sleep and performance schedules</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="p-3 border rounded bg-danger text-white h-100 d-flex flex-column">
                                <h5>These stressors can lead to:</h5>
                                <ul className="mb-0 flex-grow-1">
                                    <li>Increased anxiety and burnout</li>
                                    <li>Feelings of loneliness and depression</li>
                                    <li>Reduced sense of belonging compared to peers</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="p-3 border rounded bg-primary text-white h-100 d-flex flex-column">
                                <h5>This course helps by:</h5>
                                <ul className="mb-0 flex-grow-1">
                                    <li>Highlighting challenges among college student pilots</li>
                                    <li>Teaching peer support skills like listening, empathy, and trust-building</li>
                                    <li>Core peer support skills tailored to college pilots</li>
                                    <li>Scenario trainings</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 2,
            title: "Unique Challenges Among College Pilots",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">Academic Stress</h2>
                    <div className="row">
                        <div className="col-md-8">
                            <h4>Balancing Coursework & Flight Lessons</h4>
                            <ul className="mb-4">
                                <li>Dual responsibility: academic classes + flight training hours</li>
                                <li>Flight training and checkrides can often conflict with class and assignment deadlines</li>
                                <li>Frequent performance evaluations like stage checks and checkrides create higher stress
                                    for college pilots compared to non-pilot students</li>
                                <li>Unpredictable weather delays can lead to rescheduled flights, which can disrupt study</li>
                                <li>Financial pressure, which includes the cost of flight hours, can add stress alongside academic expenses</li>
                            </ul>
                            <div className="alert alert-info">
                                <strong>Reflection Prompt:</strong> "How do you balance your academic and flight demands?"
                            </div>
                        </div>
                        <div className="col-md-4">
                            <img src={`${process.env.PUBLIC_URL}/assets/AcademicStress.PNG`} alt="Academic Stress" className="img-fluid rounded" />
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 3,
            title: "Social Isolation Challenges",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">Social Isolation</h2>
                    <div className="row">
                        <div className="col-md-8">
                            <div className="mb-4">
                                <h4>Why It Happens</h4>
                                <ul>
                                    <li>Flight training leaves little time for a social life</li>
                                    <li>Balancing academics and flying leaves little energy for campus activities</li>
                                    <li>Weekend lessons often conflict with college events</li>
                                </ul>
                            </div>

                            <div className="mb-4">
                                <h4>What It Can Lead To</h4>
                                <ul>
                                    <li>Feeling left out or disconnected from friends</li>
                                    <li>Missing out on shared experiences</li>
                                    <li>Difficulty relating to non-pilot peers</li>
                                </ul>
                            </div>

                            <div className="alert alert-info">
                                <h5>Reflection Prompt</h5>
                                <p className="mb-0">"What's one healthy way you stay socially connected, even with a demanding flight schedule?"</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <img src={`${process.env.PUBLIC_URL}/assets/SocialIsolation.PNG`} alt="Social Isolation" className="img-fluid rounded" />
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 4,
            title: "Sleep Discipline & Financial Strain",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">Sleep Discipline & Financial Challenges</h2>
                    <div className="row">
                        <div className="col-md-6 mb-4">
                            <div className="card h-100">
                                <div className="card-header bg-secondary text-white">
                                    <h5>😴 Sleep Discipline</h5>
                                </div>
                                <div className="card-body">
                                    <ul>
                                        <li>Maintaining strict sleep schedules</li>
                                        <li>Alertness and adequate rest for safety is required</li>
                                        <li>Friends stay up late creates social pressure to join</li>
                                        <li>Saying "no" repeatedly can increase stress and isolation</li>
                                    </ul>
                                    <div className="alert alert-info mt-3">
                                        <small><strong>Self-Check:</strong> "On a scale of 1–5, how consistent is your current sleep schedule?"</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="card h-100">
                                <div className="card-header bg-warning text-dark">
                                    <h5>💰 Financial Strain</h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-12 mb-2">
                                            <div className="p-2 bg-light border rounded text-center">Tuition + Flight Training Costs</div>
                                        </div>
                                        <div className="col-12 mb-2">
                                            <div className="p-2 bg-light border rounded text-center">Flight costs can equal/exceed tuition</div>
                                        </div>
                                        <div className="col-12 mb-2">
                                            <div className="p-2 bg-light border rounded text-center">Pressure to work part-time</div>
                                        </div>
                                        <div className="col-12 mb-2">
                                            <div className="p-2 bg-light border rounded text-center">Constant worry impacts focus</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 5,
            title: "Mental Health Impacts",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">Human Factors Impacting College Pilot Performance</h2>
                    <div className="row">
                        <div className="col-md-6 mb-4">
                            <div className="card">
                                <div className="card-header bg-danger text-white">
                                    <h5>Increased Risk of Depression</h5>
                                </div>
                                <div className="card-body">
                                    <ul>
                                        <li>Social isolation from restrictions and missed peer activities</li>
                                        <li>Feelings of being "different" can reduce a student’s sense of belonging</li>
                                        <li>Loneliness increases vulnerability to depressive symptoms</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="card">
                                <div className="card-header bg-warning text-dark">
                                    <h5>Heightened Anxiety</h5>
                                </div>
                                <div className="card-body">
                                    <ul>
                                        <li>Constant pressure to perform in both academics and flight training</li>
                                        <li>Worry about checkrides, finances, and sleep schedules</li>
                                        <li>Fear of making mistakes often contributes to performance anxiety</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <div className="alert alert-info">
                                <h5>Additional Impacts:</h5>
                                <ul className="mb-0">
                                    <li><strong>Sleep-Related Mental Fatigue:</strong> Chronic tiredness can lead to irritability, reduced focus, emotional instability</li>
                                    <li><strong>Stress-Related Burnout:</strong> Emotional numbness, loss of motivation, and irritability can develop over time</li>
                                    <li><strong>Identity Strain:</strong> The fear of standing out may cause self-consciousness and withdrawal</li>
                                    <li><strong>Maladaptive Coping Risks:</strong> Students may become more prone to secrecy, denial, or unsafe behaviors</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 6,
            title: "The Role of Peer Support",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">Peer Support for College Pilots</h2>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="card h-100">
                                <div className="card-header bg-primary text-white">
                                    <h5> 🫶 Provides a Safe Space</h5>
                                </div>
                                <div className="card-body">
                                    Provides a safe, non-judgmental space to share struggles
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="card h-100">
                                <div className="card-header bg-success text-white">
                                    <h5>💬 Encourages Open Conversations</h5>
                                </div>
                                <div className="card-body">
                                    Encourages open conversations about stress, anxiety, and well-being
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-md-6">
                            <div className="card h-100">
                                <div className="card-header bg-info text-white">
                                    <h5>🧠 Reduces Stigma</h5>
                                </div>
                                <div className="card-body">
                                    Reduces stigma around asking for support
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="card h-100">
                                <div className="card-header bg-secondary text-white">
                                    <h5>🤝 Builds Resilience</h5>
                                </div>
                                <div className="card-body">
                                    Builds resilience through shared experiences
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-md-12">
                            <div className="alert alert-info">
                                <h5>🏥 Complements Professional Resources</h5>
                                <p className="mb-0">Complements professional counseling and campus resources</p>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 7,
            title: "Core Peer Support Skills",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">Core Peer Support Skills for College Pilots</h2>
                    <div className="row">
                        <div className="col-md-6 mb-4">
                            <div className="card">
                                <div className="card-header bg-primary text-white">
                                    <h5>👂 Active Listening</h5>
                                </div>
                                <div className="card-body">
                                    <ul>
                                        <li>Focus fully on what your peer is saying without judgment or interruption</li>
                                        <li>Show you understand by paraphrasing (e.g., "It sounds like you are stressed about juggling classes and flight hours.")</li>
                                        <li>Helps pilots feel heard when they may not feel understood by non-pilot peers</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="card">
                                <div className="card-header bg-success text-white">
                                    <h5>❤️ Empathy</h5>
                                </div>
                                <div className="card-body">
                                    <ul>
                                        <li>Recognize and validate the unique sacrifices pilots make</li>
                                        <li>Use compassionate language: "I get how tough it must be to watch your friends go out while you have to be up early for training."</li>
                                        <li>Builds connection by normalizing their struggles</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6 mb-4">
                            <div className="card">
                                <div className="card-header bg-warning text-dark">
                                    <h5>🔒 Confidentiality & Trust-Building</h5>
                                </div>
                                <div className="card-body">
                                    <ul>
                                        <li>Ensure peers know that conversations stay private</li>
                                        <li>Trust is essential, especially since pilots may hesitate to speak up about personal challenges</li>
                                        <li>Create a "safe space" for open sharing without judgment</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="card">
                                <div className="card-header bg-info text-white">
                                    <h5>🎯 Encouraging Healthy Coping</h5>
                                </div>
                                <div className="card-body">
                                    <ul>
                                        <li>Promote alternatives to unsafe behaviors (exercise, study groups, aviation clubs)</li>
                                        <li>Reinforce that it's okay to say "no" to peer pressure</li>
                                        <li>Normalize experiences and share personal stories</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        }
    ];

    // Quiz questions from the PDF
    const quizQuestions = [
        {
            id: 1,
            question: "A college pilot says, \"I did not fly well in training today. I will never be a good pilot.\" What should you do?",
            options: [
                "Agree with them and say they probably will not improve",
                "Make a joke about their bad landing to lighten the mood",
                "Tell them to stop complaining and just study more",
                "Remind them that learning takes time and encourage them to keep a positive attitude"
            ],
            correct: 3,
            explanation: "Supporting peers with encouragement and reminding them that learning is a process helps build resilience and confidence."
        },
        {
            id: 2,
            question: "Your classmate shares they are stressed about balancing school, flying, and a part-time job. What is the best peer support response?",
            options: [
                "Tell them to quit their job immediately",
                "Offer to listen and suggest talking through time management strategies",
                "Say everyone is stressed, so they should just get used to it",
                "Ignore them because it is not your problem"
            ],
            correct: 1,
            explanation: "Active listening and offering to help with time management strategies demonstrates effective peer support."
        },
        {
            id: 3,
            question: "A college pilot admits, \"I feel really nervous before every flight lesson.\" How should you respond?",
            options: [
                "Listen and reassure them that nervousness is common, and ask what helps them feel calmer",
                "Tell them they should stop flying if they get nervous",
                "Compare them to other students who are \"doing better\"",
                "Laugh and say they just need to \"get it together\""
            ],
            correct: 0,
            explanation: "Normalizing their feelings and asking open-ended questions helps them feel supported and encourages them to find their own positive coping strategies."
        },
        {
            id: 4,
            question: "A peer says, \"I do not know anyone in this program. I feel left out.\" What is the best supportive action?",
            options: [
                "Invite them to join a study group",
                "Tell them making friends is not important",
                "Suggest they just spend more time alone",
                "Pretend you did not hear them"
            ],
            correct: 0,
            explanation: "Taking concrete action to help them connect with others directly addresses their need for social connection and belonging."
        },
        {
            id: 5,
            question: "Your friend failed a written test and is upset. What's the most supportive thing to do?",
            options: [
                "Compare them to someone that passed the test and say they should be like that person",
                "Encourage them, remind them that one test does not define their future, and offer to study together",
                "Say nothing and walk away",
                "Post about their failure on social media to get laughs"
            ],
            correct: 1,
            explanation: "Providing encouragement, perspective, and practical support shows genuine care and helps them move forward constructively."
        },
        {
            id: 6,
            question: "A college pilot confides in you, \"I do not want to tell anyone I am struggling because they'll think less of me.\" How should you respond?",
            options: [
                "Tell them they are right and others probably will think less of them",
                "Reassure them that asking for help is a sign of strength",
                "Change the subject quickly to avoid awkwardness",
                "Tell them to keep quiet so nobody knows"
            ],
            correct: 1,
            explanation: "Reframing help-seeking as strength rather than weakness helps reduce stigma and encourages them to seek the support they need."
        },
        {
            id: 7,
            question: "Your classmate seems unusually quiet and withdrawn. What is the best peer support approach?",
            options: [
                "Check in kindly and ask if they are doing okay",
                "Ignore them since it is not your business",
                "Make jokes about them being quiet",
                "Spread rumors that they are having problems"
            ],
            correct: 0,
            explanation: "A caring check-in shows you notice and care about their Well-being, which can be the first step in providing support."
        },
        {
            id: 8,
            question: "A college pilot says, \"I am afraid of asking my instructor questions because they will think I am not smart.\" How can you respond?",
            options: [
                "Mock them for not already knowing everything",
                "Agree that it is better to stay quiet",
                "Tell them pilots don't ask questions",
                "Remind them that asking questions shows they want to learn and grow"
            ],
            correct: 3,
            explanation: "Reframing question-asking as a positive learning behavior helps reduce their fear and encourages active engagement in their training."
        },
        {
            id: 9,
            question: "A college pilot says, \"I feel like I am the only one struggling in this program.\" What is a supportive reply?",
            options: [
                "Tell them many students face challenges, and they are not alone",
                "Agree that they are probably the weakest student",
                "Say, \"Just quit if it is too hard\"",
                "Tell them not to talk about it anymore"
            ],
            correct: 0,
            explanation: "Normalizing struggles and reassuring them they're not alone helps reduce isolation and builds connection with the broader pilot community."
        },
        {
            id: 10,
            question: "You notice your peer looks very stressed before a flight. What is the best supportive step?",
            options: [
                "Laugh at them",
                "Tell them to stop being dramatic",
                "Ask if they want to talk about what is on their mind",
                "Avoid them so you do not have to deal with it"
            ],
            correct: 2,
            explanation: "Offering to listen without judgment provides them with an opportunity to share their concerns and feel supported."
        }
    ];

    const totalSlides = slides.length + quizQuestions.length;
    const isQuizSection = currentSlide >= slides.length;
    const currentQuizIndex = currentSlide - slides.length;

    // Load progress when user is authenticated
    useEffect(() => {
        const loadUserProgress = async () => {
            if (user && !progressLoaded) {
                await loadProgress();
            }
        };

        if (!loading && user) {
            loadUserProgress();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, loading, progressLoaded]);

    const loadProgress = async () => {
        try {
            const progress = await loadQuizProgress(user.uid, 'college-student-course');
            if (progress) {
                // Set state values from loaded progress
                const loadedSlide = progress.currentSlide || 0;
                const loadedAnswers = progress.quizAnswers || {};
                const loadedResults = progress.showResults || false;

                setCurrentSlide(loadedSlide);
                setQuizAnswers(loadedAnswers);
                setShowResults(loadedResults);
            }
            // Mark progress as loaded AFTER state has been set
            setProgressLoaded(true);
        } catch (error) {
            console.error('Error loading progress:', error);
            setProgressLoaded(true);
        }
    };


    // Save progress when slide changes (with debouncing)
    // IMPORTANT: Only save AFTER initial load is complete to prevent overwriting with initial state
    useEffect(() => {
        // Skip the very first render after progress is loaded to avoid saving the loaded value
        if (isInitialMount.current && progressLoaded) {
            isInitialMount.current = false;
            return;
        }

        // Only save after progress has been loaded AND this is not the initial mount
        if (user && progressLoaded && !isInitialMount.current && currentSlide >= 0) {
            setSaving(true);
            const timeoutId = setTimeout(async () => {
                try {
                    await saveSlideProgress(user.uid, 'college-student-course', currentSlide);
                } catch (error) {
                    console.error('Failed to save progress:', error);
                } finally {
                    setSaving(false);
                }
            }, 500); // Debounce saves by 500ms

            return () => {
                clearTimeout(timeoutId);
                setSaving(false);
            };
        }
    }, [currentSlide, user, progressLoaded]);

    const handleNext = () => {
        if (currentSlide < totalSlides - 1) {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const handlePrevious = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    // Scroll to top whenever slide changes (after DOM update)
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentSlide]);

    const handleQuizAnswer = async (questionId, answerIndex) => {
        const question = quizQuestions.find(q => q.id === questionId);
        const isCorrect = answerIndex === question.correct;

        const newQuizAnswers = {
            ...quizAnswers,
            [questionId]: {
                selected: answerIndex,
                correct: isCorrect
            }
        };

        setQuizAnswers(newQuizAnswers);

        // Save answer to Firestore
        if (user) {
            try {
                await saveQuizAnswer(user.uid, 'college-student-course', questionId, answerIndex, isCorrect);
            } catch (error) {
                console.error('Failed to save quiz answer:', error);
            }
        }

        // Check if quiz is complete
        const answeredQuestions = Object.keys(newQuizAnswers).length;

        if (answeredQuestions === quizQuestions.length) {
            setShowResults(true);

            // Mark quiz as completed if user passed
            const correctAnswers = Object.values(newQuizAnswers).filter(answer => answer.correct).length;
            if (user && correctAnswers === quizQuestions.length) {
                try {
                    await markQuizCompleted(user.uid, 'college-student-course', {
                        currentSlide,
                        quizAnswers: newQuizAnswers,
                        showResults: true
                    });
                } catch (error) {
                    console.error('Failed to mark quiz as completed:', error);
                }
            }
        }
    };

    const calculateScore = () => {
        const totalQuestions = quizQuestions.length;
        const correctAnswers = Object.values(quizAnswers).filter(answer => answer.correct).length;

        return {
            correct: correctAnswers,
            total: totalQuestions,
            percentage: Math.round((correctAnswers / totalQuestions) * 100)
        };
    };

    const handleCompleteQuiz = async () => {
        const score = calculateScore();
        if (score.percentage === 100) {
            setShowCertificate(true);
            if (user) {
                try {
                    await markQuizCompleted(user.uid, 'college-student-course', score);
                } catch (error) {
                    console.error('Error marking quiz completed:', error);
                }
            }
        }
    };

    const resetQuiz = async () => {
        setQuizAnswers({});
        setShowResults(false);
        setCurrentSlide(slides.length); // Start at first quiz question
        setShowCertificate(false);

        // Also reset in database
        if (user) {
            try {
                await saveQuizProgress(user.uid, 'college-student-course', {
                    currentSlide: slides.length,
                    quizAnswers: {},
                    showResults: false,
                    completedAt: null
                });
            } catch (error) {
                console.error('Failed to reset quiz in database:', error);
            }
        }
    };

    const renderSlide = () => {
        if (isQuizSection) {
            const question = quizQuestions[currentQuizIndex];
            const userAnswer = quizAnswers[question.id];

            return (
                <div>
                    <h2 className="mb-4">Question {question.id} of {quizQuestions.length}</h2>
                    <div className="card quiz-card">
                        <div className="card-body">
                            <h4 className="card-title mb-4">{question.question}</h4>
                            <div className="d-grid gap-2">
                                {question.options.map((option, index) => {
                                    let className = "btn btn-outline-primary text-start";
                                    let disabled = false;

                                    if (userAnswer) {
                                        disabled = true;
                                        if (index === question.correct) {
                                            className = "btn btn-success text-start";
                                        } else if (index === userAnswer.selected && !userAnswer.correct) {
                                            className = "btn btn-danger text-start";
                                        } else {
                                            className = "btn btn-outline-secondary text-start";
                                        }
                                    }

                                    return (
                                        <button
                                            key={index}
                                            className={className}
                                            onClick={() => handleQuizAnswer(question.id, index)}
                                            disabled={disabled}
                                        >
                                            {String.fromCharCode(97 + index)}) {option}
                                        </button>
                                    );
                                })}
                            </div>

                            {userAnswer && (
                                <div className={`alert quiz-explanation mt-4 ${userAnswer.correct ? 'alert-success' : 'alert-warning'}`}>
                                    <strong>{userAnswer.correct ? 'Correct!' : 'Incorrect.'}</strong>
                                    <p className="mb-0 mt-2">{question.explanation}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        } else {
            const currentSlideData = slides[currentSlide];
            return (
                <div className="card slide-card">
                    <div className="card-body">
                        {currentSlideData.content}
                    </div>
                </div>
            );
        }
    };

    const renderQuizResults = () => {
        const score = calculateScore();
        const allQuestionsAnswered = Object.keys(quizAnswers).length === quizQuestions.length;

        return (
            <div className="card">
                <div className="card-header bg-primary text-white">
                    <h2 className="mb-0">Quiz Results</h2>
                </div>
                <div className="card-body">
                    <div className="text-center mb-4">
                        <h3>Your Score: {score.correct}/{score.total} ({score.percentage}%)</h3>
                        {score.percentage === 100 ? (
                            <div className="alert alert-success">
                                <h4>Congratulations! 🎉</h4>
                                <p>You've successfully completed the College Pilot Peer Support Course!</p>
                                <button
                                    className="btn btn-success btn-lg"
                                    onClick={handleCompleteQuiz}
                                >
                                    Get Your Certificate
                                </button>
                            </div>
                        ) : (
                            <div className="alert alert-warning">
                                <h4>Almost there!</h4>
                                <p>You need to get all questions correct to pass. Please review and try again.</p>
                                <button
                                    className="btn btn-warning"
                                    onClick={resetQuiz}
                                >
                                    Retake Quiz
                                </button>
                            </div>
                        )}
                    </div>

                    {allQuestionsAnswered && (
                        <div className="quiz-review">
                            <h4>Review Your Answers:</h4>
                            {quizQuestions.map((question, index) => {
                                const userAnswer = quizAnswers[question.id];
                                return (
                                    <div key={question.id} className="question-card">
                                        <div className="question-header">
                                            Question {index + 1} of {quizQuestions.length}
                                        </div>
                                        <div className="question-body">
                                            <div className="question-text">
                                                {question.question}
                                            </div>
                                            <div className="answer-options">
                                                {question.options.map((option, optionIndex) => {
                                                    let badgeClass = "bg-secondary";

                                                    if (userAnswer && userAnswer.selected === optionIndex) {
                                                        badgeClass = userAnswer.correct ? "bg-success" : "bg-danger";
                                                    } else if (optionIndex === question.correct) {
                                                        badgeClass = "bg-success";
                                                    }

                                                    return (
                                                        <div key={optionIndex} className="answer-option">
                                                            <span className={`badge ${badgeClass} flex-shrink-0`}>
                                                                {String.fromCharCode(65 + optionIndex)}
                                                            </span>
                                                            <span>{option}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className="explanation-section">
                                                <small>
                                                    <strong>Explanation:</strong> {question.explanation}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Authentication functions
    const closeAuthModal = () => {
        setShowAuthModal(false);
        setIsSignUp(false);
        setAuthForm({ name: '', email: '', password: '', confirmPassword: '' });
        setAuthError('');
        setIsAuthLoading(false);
    };

    const closePasswordResetModal = () => {
        setShowPasswordResetModal(false);
        setResetEmail('');
        setResetError('');
        setResetSuccess(false);
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setIsAuthLoading(true);
        setResetError('');

        try {
            await sendPasswordResetEmail(auth, resetEmail);
            setResetSuccess(true);
        } catch (error) {
            let errorMessage = 'An error occurred';

            switch (error.code) {
                case 'auth/user-not-found':
                    errorMessage = 'No account found with this email address';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address';
                    break;
                case 'auth/too-many-requests':
                    errorMessage = 'Too many requests. Please try again later.';
                    break;
                default:
                    errorMessage = error.message;
            }

            setResetError(errorMessage);
        }

        setIsAuthLoading(false);
    };

    const handleFormChange = (e) => {
        setAuthForm({
            ...authForm,
            [e.target.name]: e.target.value
        });
        setAuthError('');
    };

    const handleGoogleSignIn = () => {
        signInWithPopup(auth, googleProvider)
            .then(() => {
                closeAuthModal();
            })
            .catch((err) => {
                console.error('Google sign in error:', err);
                setAuthError('Failed to sign in with Google. Please try again.');
            });
    };

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        setIsAuthLoading(true);
        setAuthError('');

        try {
            if (isSignUp) {
                // Sign up validation
                if (!authForm.name.trim()) {
                    setAuthError('Name is required');
                    setIsAuthLoading(false);
                    return;
                }
                if (authForm.password !== authForm.confirmPassword) {
                    setAuthError('Passwords do not match');
                    setIsAuthLoading(false);
                    return;
                }
                if (authForm.password.length < 6) {
                    setAuthError('Password must be at least 6 characters');
                    setIsAuthLoading(false);
                    return;
                }

                const result = await createUserWithEmailAndPassword(auth, authForm.email, authForm.password);

                // Update the user's profile with their name
                await updateProfile(result.user, {
                    displayName: authForm.name.trim()
                });
            } else {
                // Sign in
                await signInWithEmailAndPassword(auth, authForm.email, authForm.password);
            }

            closeAuthModal();

            // Scroll to top after successful authentication
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        } catch (error) {
            let errorMessage = 'An error occurred';

            switch (error.code) {
                case 'auth/email-already-in-use':
                    errorMessage = 'This email is already registered';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address';
                    break;
                case 'auth/weak-password':
                    errorMessage = 'Password is too weak';
                    break;
                case 'auth/user-not-found':
                    errorMessage = 'No account found with this email';
                    break;
                case 'auth/wrong-password':
                    errorMessage = 'Incorrect password';
                    break;
                case 'auth/invalid-credential':
                    errorMessage = 'Invalid email or password';
                    break;
                default:
                    errorMessage = error.message;
            }

            setAuthError(errorMessage);
        }

        setIsAuthLoading(false);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{minHeight: '50vh'}}>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <>
            <div className='auth-screen-container'>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-md-6 text-center">
                            <img src="/assets/logo.png" alt="PilotHelper Logo" className="auth-logo" />
                            <h2 className="mb-4">Sign in Required</h2>
                            <p className="lead mb-4">
                                Please sign in to access the College Pilot Peer Support Course.
                                Your progress will be saved and you can resume from any device.
                            </p>

                            <div className="d-grid gap-2">
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={() => setShowAuthModal(true)}
                                >
                                    <i className="fas fa-user me-2"></i>
                                    Sign In / Sign Up
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Authentication Modal */}
                    {showAuthModal && (
                        <div
                            className="modal show d-block"
                            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                            onClick={closeAuthModal}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="college-auth-modal-title"
                        >
                            <div
                                className="modal-dialog modal-dialog-centered"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="modal-content">
                                    <div className="modal-header border-0">
                                        <h5 id="college-auth-modal-title" className="modal-title">
                                            {isSignUp ? 'Create Account' : 'Sign In'}
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={closeAuthModal}
                                            aria-label="Close"
                                        ></button>
                                    </div>
                                    <div className="modal-body">
                                        {/* Google Sign In Button */}
                                        <button
                                            onClick={handleGoogleSignIn}
                                            className="btn btn-outline-dark w-100 mb-3 d-flex align-items-center justify-content-center"
                                            disabled={isAuthLoading}
                                        >
                                            <img
                                                src="https://developers.google.com/identity/images/g-logo.png"
                                                alt="Google"
                                                width="20"
                                                className="me-2"
                                            />
                                            Continue with Google
                                        </button>

                                        <div className="text-center mb-3">
                                            <span className="text-muted">or</span>
                                        </div>

                                        {/* Error Message */}
                                        {authError && (
                                            <div className="alert alert-danger" role="alert">
                                                {authError}
                                            </div>
                                        )}

                                        {/* Email/Password Form */}
                                        <form onSubmit={handleEmailAuth}>
                                            {isSignUp && (
                                                <div className="mb-3">
                                                    <label htmlFor="name" className="form-label">Full Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="name"
                                                        name="name"
                                                        value={authForm.name}
                                                        onChange={handleFormChange}
                                                        required
                                                        disabled={isAuthLoading}
                                                        placeholder="Enter your full name"
                                                    />
                                                </div>
                                            )}
                                            <div className="mb-3">
                                                <label htmlFor="email" className="form-label">Email</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    id="email"
                                                    name="email"
                                                    value={authForm.email}
                                                    onChange={handleFormChange}
                                                    required
                                                    disabled={isAuthLoading}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="password" className="form-label">Password</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    id="password"
                                                    name="password"
                                                    value={authForm.password}
                                                    onChange={handleFormChange}
                                                    required
                                                    disabled={isAuthLoading}
                                                    minLength="6"
                                                />
                                            </div>
                                            {isSignUp && (
                                                <div className="mb-3">
                                                    <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                                                    <input
                                                        type="password"
                                                        className="form-control"
                                                        id="confirmPassword"
                                                        name="confirmPassword"
                                                        value={authForm.confirmPassword}
                                                        onChange={handleFormChange}
                                                        required
                                                        disabled={isAuthLoading}
                                                        minLength="6"
                                                    />
                                                </div>
                                            )}
                                            <button
                                                type="submit"
                                                className="btn btn-primary w-100 mb-3"
                                                disabled={isAuthLoading}
                                            >
                                                {isAuthLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        {isSignUp ? 'Creating Account...' : 'Signing In...'}
                                                    </>
                                                ) : (
                                                    isSignUp ? 'Create Account' : 'Sign In'
                                                )}
                                            </button>
                                        </form>

                                        {/* Forgot Password Link - Only show during sign in */}
                                        {!isSignUp && (
                                            <div className="text-center mb-3">
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 text-decoration-none"
                                                    onClick={() => {
                                                        closeAuthModal();
                                                        setShowPasswordResetModal(true);
                                                    }}
                                                    style={{fontSize: '0.9rem'}}
                                                >
                                                    Forgot your password?
                                                </button>
                                            </div>
                                        )}

                                        {/* Toggle between sign in and sign up */}
                                        <div className="text-center">
                                            <span className="text-muted">
                                                {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-link p-0"
                                                onClick={() => {
                                                    setIsSignUp(!isSignUp);
                                                    setAuthForm({ name: '', email: '', password: '', confirmPassword: '' });
                                                    setAuthError('');
                                                }}
                                                disabled={isAuthLoading}
                                            >
                                                {isSignUp ? 'Sign In' : 'Sign Up'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

                {/* Password Reset Modal */}
                {showPasswordResetModal && (
                    <div
                        className="modal show d-block"
                        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                        onClick={closePasswordResetModal}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="college-reset-modal-title"
                    >
                        <div
                            className="modal-dialog modal-dialog-centered"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-content">
                                <div className="modal-header border-0">
                                    <h5 id="college-reset-modal-title" className="modal-title">
                                        Reset Password
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={closePasswordResetModal}
                                        aria-label="Close"
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    {!resetSuccess ? (
                                        <>
                                            <p className="text-muted mb-4">
                                                Enter your email address and we'll send you a link to reset your password.
                                            </p>

                                            {/* Error Message */}
                                            {resetError && (
                                                <div className="alert alert-danger" role="alert">
                                                    {resetError}
                                                </div>
                                            )}

                                            <form onSubmit={handlePasswordReset}>
                                                <div className="mb-3">
                                                    <label htmlFor="resetEmail" className="form-label">Email Address</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        id="resetEmail"
                                                        value={resetEmail}
                                                        onChange={(e) => setResetEmail(e.target.value)}
                                                        required
                                                        disabled={isAuthLoading}
                                                        placeholder="Enter your email address"
                                                    />
                                                </div>
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary w-100 mb-3"
                                                    disabled={isAuthLoading}
                                                >
                                                    {isAuthLoading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Sending Reset Email...
                                                        </>
                                                    ) : (
                                                        'Send Reset Email'
                                                    )}
                                                </button>
                                            </form>

                                            <div className="text-center">
                                                <span className="text-muted">
                                                    Remember your password?
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 ms-1"
                                                    onClick={() => {
                                                        closePasswordResetModal();
                                                        setShowAuthModal(true);
                                                    }}
                                                    disabled={isAuthLoading}
                                                >
                                                    Sign In
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center">
                                            <div className="text-success mb-3">
                                                <i className="fas fa-check-circle fa-3x"></i>
                                            </div>
                                            <h5 className="mb-3">Reset Email Sent!</h5>
                                            <p className="text-muted mb-4">
                                                We've sent a password reset link to <strong>{resetEmail}</strong>.
                                                Check your email and follow the instructions to reset your password.
                                            </p>
                                            <p className="text-muted small mb-4">
                                            Don't see the email? <strong>Check your spam folder or try again with a different email address.</strong>
                                            </p>
                                            <button
                                                className="btn btn-primary"
                                                onClick={closePasswordResetModal}
                                            >
                                                Done
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    if (showCertificate) {
        return (
            <CertificateGenerator
                user={user}
                courseType="college"
                onClose={() => setShowCertificate(false)}
            />
        );
    }

    if (showResults) {
        const score = calculateScore();
        const allQuestionsAnswered = Object.keys(quizAnswers).length === quizQuestions.length;

        // If no questions have been answered yet, show the quiz instead of results
        if (Object.keys(quizAnswers).length === 0) {
            // Fall through to show quiz questions below
        } else {
            return (
                <div className="peer-support-course-container">
                    <div className="container py-5">
                        <div className="row justify-content-center">
                            <div className="col-md-8">
                                <div className="card">
                                    <div className="card-header bg-primary text-white">
                                        <h2 className="mb-0">Quiz Results</h2>
                                    </div>
                                    <div className="card-body">
                                        <div className="text-center mb-4">
                                            <h3>Your Score: {score.correct}/{score.total} ({score.percentage}%)</h3>
                                            {score.percentage === 100 ? (
                                                <div className="alert alert-success">
                                                    <h4>Congratulations! 🎉</h4>
                                                    <p>You've successfully completed the College Pilot Peer Support Course!</p>
                                                    <button
                                                        className="btn btn-success btn-lg"
                                                        onClick={handleCompleteQuiz}
                                                    >
                                                        Get Your Certificate
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="alert alert-warning">
                                                    <h4>Almost there!</h4>
                                                    <p>You need to get all questions correct to pass. Please review and try again.</p>
                                                    <button
                                                        className="btn btn-warning"
                                                        onClick={resetQuiz}
                                                    >
                                                        Retake Quiz
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {allQuestionsAnswered && (
                                            <div className="quiz-review">
                                                <h4>Review Your Answers:</h4>
                                                {quizQuestions.map((question, index) => {
                                                    const userAnswer = quizAnswers[question.id];
                                                    return (
                                                        <div key={question.id} className="mb-4 border rounded p-3">
                                                            <h6>Question {index + 1}: {question.question}</h6>
                                                            <div className="row">
                                                                {question.options.map((option, optionIndex) => {
                                                                    let className = "col-12 mb-2";
                                                                    let badgeClass = "bg-secondary";

                                                                    if (userAnswer && userAnswer.selected === optionIndex) {
                                                                        badgeClass = userAnswer.correct ? "bg-success" : "bg-danger";
                                                                    } else if (optionIndex === question.correct) {
                                                                        badgeClass = "bg-success";
                                                                    }

                                                                    return (
                                                                        <div key={optionIndex} className={className}>
                                                                            <span className={`badge ${badgeClass} me-2`}>
                                                                                {String.fromCharCode(65 + optionIndex)}
                                                                            </span>
                                                                            {option}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                            <div className="mt-2">
                                                                <small className="text-muted">
                                                                    <strong>Explanation:</strong> {question.explanation}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    }

    return (
        <>
        <div className="peer-support-course-container">
            <div className="container py-5">
                {/* Progress bar */}
                <div className="progress mb-4">
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{width: `${((currentSlide + 1) / totalSlides) * 100}%`}}
                        aria-valuenow={currentSlide + 1}
                        aria-valuemin="0"
                        aria-valuemax={totalSlides}
                    >
                        {Math.round(((currentSlide + 1) / totalSlides) * 100)}%
                    </div>
                </div>
                <small className="text-muted progress-info">
                    Slide {currentSlide + 1} of {totalSlides}
                    {isQuizSection && " (Quiz Section)"}
                </small>


                {/* Course Content */}
                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        {showResults ? renderQuizResults() : renderSlide()}
                    </div>
                </div>

                {/* Navigation */}
                {!showResults && (
                    <div className='course-navigation'>
                        <div className="d-flex justify-content-between mt-4">
                            <button
                                className="btn btn-secondary"
                                onClick={handlePrevious}
                                disabled={currentSlide === 0}
                            >
                                <i className="fas fa-chevron-left me-2"></i>
                                Previous
                            </button>

                            <button
                                className="btn btn-primary"
                                onClick={handleNext}
                                disabled={currentSlide === totalSlides - 1 || (isQuizSection && !quizAnswers[quizQuestions[currentQuizIndex].id])}
                            >
                                Next
                                <i className="fas fa-chevron-right ms-2"></i>
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>

        {/* Certificate Generator Modal */}
        {showCertificate && (
            <CertificateGenerator
                user={user}
                courseType="college"
                onClose={() => setShowCertificate(false)}
            />
        )}
    </>
    );
};

export default CollegeStudentCourse;
